import {
  AutomationConditionType,
  AutomationConditionValueInput,
  ConnectionApplicationStatus,
  DayOfWeek,
  EventTaskStatus,
  ReadableInvoiceStatus,
  ReadableProposalStatus,
} from '@/gql/graphql';
import { assertUnreachable } from '@payaca/utilities/guards';
import { exhaustiveArray } from '@payaca/utilities/types';

type ValueKind =
  | 'string'
  | 'boolean'
  | 'readableProposalStatus'
  | 'readableInvoiceStatus'
  | 'eventTaskStatus'
  | 'dayOfWeek'
  | 'connectionApplicationStatus'
  | 'pipeline'
  | 'pipelineStageName'
  | 'tag';

/**
 * Maps each condition type to the kind of value input it needs.
 */
export const CONDITION_TYPE_VALUE_KIND: Record<
  AutomationConditionType,
  ValueKind
> = {
  AssigneesPhoneCondition: 'string',
  ConnectionApplicationStatusCondition: 'connectionApplicationStatus',
  CustomerEmailCondition: 'string',
  CustomerNameCondition: 'string',
  CustomerPhoneCondition: 'string',
  EventAssigneesEmailCondition: 'string',
  EventDoesNotHaveTagCondition: 'tag',
  EventHasTagCondition: 'tag',
  EventTaskStatusCondition: 'eventTaskStatus',
  InvoiceIsDepositCondition: 'boolean',
  InvoiceStatusCondition: 'readableInvoiceStatus',
  LocationContactsEmailCondition: 'string',
  LocationContactsPhoneCondition: 'string',
  NewPipelineStageNameCondition: 'pipelineStageName',
  NewlyAddedTagCondition: 'tag',
  OldPipelineStageNameCondition: 'pipelineStageName',
  ProjectDoesNotHaveTagCondition: 'tag',
  ProjectHasTagCondition: 'tag',
  ProjectPipelineCondition: 'pipeline',
  ProjectPipelineStageNameCondition: 'pipelineStageName',
  ProjectSiteAddressContactsEmailCondition: 'string',
  ProjectSiteAddressContactsPhoneCondition: 'string',
  ProposalStatusCondition: 'readableProposalStatus',
  TriggeredDayOfWeekCondition: 'dayOfWeek',
  UserEmailCondition: 'string',
};

type TranslationFn = (key: string) => string;

/**
 * Builds value options for a given value kind using i18n translations.
 * Returns null for dynamic kinds (pipeline, pipelineStageName, tag) and string kind.
 */
export function getStaticValueOptions(
  kind: ValueKind,
  t: TranslationFn
): { label: string; value: string }[] | null {
  switch (kind) {
    case 'boolean':
      return [
        { label: t('conditions.boolean.true'), value: 'true' },
        { label: t('conditions.boolean.false'), value: 'false' },
      ];
    case 'readableProposalStatus':
      return exhaustiveArray<ReadableProposalStatus>()([
        'ACCEPTED',
        'ARCHIVED',
        'DECLINED',
        'DEPOSIT_DUE',
        'DEPOSIT_PAID',
        'EXPIRED',
        'INACTIVE',
        'PAYMENT_PENDING',
        'SENT',
      ]).map((s) => ({ label: t(`conditions.statuses.${s}`), value: s }));
    case 'readableInvoiceStatus':
      // Filtered to exclude v1 Invoice (jobs) statuses
      return (['PAID', 'SENT'] satisfies ReadableInvoiceStatus[]).map((s) => ({
        label: t(`conditions.statuses.${s}`),
        value: s,
      }));
    case 'eventTaskStatus':
      return exhaustiveArray<EventTaskStatus>()([
        'COMPLETED',
        'NOT_STARTED',
        'NO_TASKS',
        'PARTIALLY_COMPLETED',
      ]).map((s) => ({ label: t(`conditions.statuses.${s}`), value: s }));
    case 'dayOfWeek':
      return exhaustiveArray<DayOfWeek>()([
        'Monday',
        'Tuesday',
        'Wednesday',
        'Thursday',
        'Friday',
        'Saturday',
        'Sunday',
      ]).map((d) => ({ label: t(`conditions.dayOfWeek.${d}`), value: d }));
    case 'connectionApplicationStatus':
      return exhaustiveArray<ConnectionApplicationStatus>()([
        'AUTO_APPROVED',
        'AWAITING_ASSESSMENT',
        'COMMISSIONING_ACCEPTED',
        'COMMISSIONING_SUBMITTED',
        'COMMISSIONING_UPDATE_REQUIRED',
        'DNO_APPROVED',
        'DNO_APPROVED_WITH_LIMIT',
        'ESCALATED_TO_DNO',
        'EX_MPAN_PROVIDED',
        'FAILED',
        'FAILED_INVALID_CUT_OUT_IMAGE',
        'FAILED_TOO_LARGE',
        'FURTHER_INFORMATION_REQUIRED',
        'IMAGE_SCAN_FAILED',
        'RE_AUTO_APPROVED',
        'RE_ESCALATED_TO_DNO',
        'SCANNING_CUT_OUT_IMAGE',
        'UNDER_ASSESSMENT',
      ]).map((s) => ({ label: t(`conditions.statuses.${s}`), value: s }));
    default:
      return null;
  }
}

/**
 * Converts a raw string value + condition type into the GraphQL
 * AutomationConditionValueInput shape.
 */
export function buildConditionValueInput(
  conditionType: AutomationConditionType,
  rawValue: string
): AutomationConditionValueInput | null {
  if (!rawValue) return null;

  const kind = CONDITION_TYPE_VALUE_KIND[conditionType];
  // Casts below are safe: rawValue originates from a Select whose options are
  // built from the same enum literal arrays in getStaticValueOptions. The
  // server also validates via Zod before persisting.
  switch (kind) {
    case 'string':
    case 'pipelineStageName':
    case 'tag':
      return { stringValue: rawValue };
    case 'boolean':
      return { booleanValue: rawValue === 'true' };
    case 'readableProposalStatus':
      return { readableProposalStatus: rawValue as ReadableProposalStatus };
    case 'readableInvoiceStatus':
      return { readableInvoiceStatus: rawValue as ReadableInvoiceStatus };
    case 'eventTaskStatus':
      return { eventTaskStatus: rawValue as EventTaskStatus };
    case 'dayOfWeek':
      return { dayOfWeek: rawValue as DayOfWeek };
    case 'connectionApplicationStatus':
      return {
        connectionApplicationStatus: rawValue as ConnectionApplicationStatus,
      };
    case 'pipeline':
      return { pipeline: { id: rawValue } };
    default:
      return assertUnreachable(kind);
  }
}

/**
 * Condition types that are always auto-added by the backend for pipeline
 * automations and should not appear in the user-facing condition list.
 */
export const EXCLUDED_CONDITION_TYPES: AutomationConditionType[] = [
  'ProjectPipelineCondition',
];
